import React, {useCallback, useEffect, useMemo, useState} from 'react';
import './App.css';
import {ApolloClient, ApolloProvider, InMemoryCache, useApolloClient} from '@apollo/client';
import {ArtworkBrowser, queryMasterToken} from "./ArtworkBrowser";
import {Artwork, ErrorBoundary} from "./Artwork";
import {css} from "@emotion/css";
import {Controls} from './Controls';
import {ArtworkState} from "./layered_static_v1";
import { useDebounce } from 'use-debounce';



const client = new ApolloClient({
  uri: 'https://api.thegraph.com/subgraphs/name/avolabs-io/async_art_v2',
  cache: new InMemoryCache()
});

function App() {
  return <ApolloProvider client={client}>
    <Main />
  </ApolloProvider>
}



function useChainValues(master: any) {
  return useMemo(() => {
    if (!master?.layers) {
      return null;
    }
    let state: ArtworkState = {};
    for (const layer of master?.layers ?? []) {
      layer.levers.forEach((lever: any, idx: number) => {
        state[`${layer.tokenDetails.tokenId}/${idx}`] = lever.currentValue;
      })
    }

    return state;
  }, [master?.layers]);
}

function Main() {
  const client = useApolloClient();
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentArtwork, setCurrentArtwork] = useState<any|null>(null);
  const chainValues = useChainValues(currentArtwork);
  const [userValues, setUserValues] = React.useState<ArtworkState>({});
  const [manifest, setManifest] = React.useState<any>(null);

  const handleTokenIdChanged = useCallback(async (tokenId: number|undefined) => {
    if (!tokenId) {
      return;
    }
    setLoading(true);
    try {
      const chainInfo = await queryMasterToken(client, tokenId);
      if (!chainInfo) {
        setError("No artwork with id " + tokenId + " found.")
        return;
      }
      const manifestResponse = await fetch(`https://gateway.pinata.cloud/ipfs/${chainInfo?.tokenDetails.uri}`);
      const manifest = await manifestResponse.json();

      setError("");
      setCurrentArtwork(chainInfo);
      setUserValues({});
      setManifest(manifest);
    }
    catch(e) {
      setError(`Error loading: ${JSON.stringify(e)}`)
    }
    finally {
      setLoading(false);
    }
  }, [client]);

  const handleStateUpdate = (state: ArtworkState) => {
    setUserValues(state);
  };

  const [debouncedUserValues] = useDebounce(userValues, 500);

  return (
    <div className={css`
      height: 100%;
      width: 100%;
      position: absolute;
      display: flex;
      flex-direction: column;
    `}>
      <InputBar onTokenChanged={handleTokenIdChanged} />

      {isLoading ? <div>
        Loading...
      </div> : null}

      {error ? <div>
        Error: {error}
      </div> : null}

      {(currentArtwork && !isLoading && !error) ? <div className={css`
        display: flex;
        flex-direction: row;
        flex: 1;
        overflow: hidden;
      `}>
        <ErrorBoundary>
          <Artwork
            master={currentArtwork}
            manifest={manifest}
            chainValues={chainValues!}
            userValues={debouncedUserValues}
            className={css`flex: 1;`}
          />
        </ErrorBoundary>
        <Controls
          master={currentArtwork}
          manifest={manifest}
          chainValues={chainValues!}
          userValues={userValues}
          onUserValuesChange={handleStateUpdate}
        />
      </div> : null}
    </div>
  );
}


function InputBar(props: {
  onTokenChanged: (tokenId: number|undefined) => void
}) {
  const [text, setText] = useState("");
  const tokenId = useMemo(() => {
    // https://async.art/art/master/0xb6dae651468e9593e4581705a09c10a76ac1e0c8-83
    const match = text.match(/(\d+)$/);
    if (match && match[0]) {
      return parseInt(match[0]);
    }
  }, [text]);

  const [debouncedTokenId] = useDebounce(tokenId, 500);
  const {onTokenChanged} = props;
  useEffect(() => {
    onTokenChanged(debouncedTokenId);
  }, [onTokenChanged, debouncedTokenId]);

  return <div className={css`
    padding: 5px;
    background-color: #efefef;
    display: flex;
    flex-direction: row;
  `}>
    <input  className={css`
      padding: 0.5em;
      flex: 1;
      outline: none;
      font-size: 18px;
      border: 1px solid ${text && !tokenId ? "red" : 'silver'};
    `} placeholder={"Paste the URL of an artwork from the AsyncArt website."}
      value={text}
      onChange={e => {
        setText(e.currentTarget.value)
      }}
    />
  </div>
}


export default App;
